import { useCallback, useEffect, useState } from 'react';
import { useMedplum, useSubscription } from '@medplum/react';
import { Patient, MedicationStatement } from '@medplum/fhirtypes';

export interface MedicationsData {
  readonly medications: MedicationStatement[];
}

export const useMedicationsData = (patient?: Patient, enabled = true) => {
  const medplum = useMedplum();
  const [data, setData] = useState<MedicationsData>({
    medications: [],
  });
  const [loading, setLoading] = useState(false);

  const fetchMedications = useCallback(async () => {
    if (!patient?.id) {
      setData({ medications: [] });
      return;
    }

    medplum.invalidateSearches('MedicationStatement');

    setLoading(true);
    try {
      const ref = `Patient/${patient.id}`;
      const searchMeta = { _count: 100, _sort: '-_lastUpdated' };

      const medications = await medplum.searchResources('MedicationStatement', {
        subject: ref,
        ...searchMeta,
      });

      setData({ medications });
    } catch (error) {
      console.error('Error loading medications:', error);
    } finally {
      setLoading(false);
    }
  }, [patient?.id, medplum]);

  useEffect(() => {
    if (!enabled) {
      setData({ medications: [] });
      return;
    }
    fetchMedications();
  }, [fetchMedications, enabled]);

  useSubscription(
    enabled && patient?.id
      ? `MedicationStatement?subject=Patient/${patient.id}`
      : '',
    () => {
      fetchMedications();
    }
  );

  return { data, loading, refetch: fetchMedications };
};
